import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
} from '@shared/components';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ExamAssigningList } from '../exam-assigning-list';

const ExamAssigningSchema = z.object({
  examCourseCodes: z.array(z.string()).min(1),
});

type ExamAssigningFormInput = z.infer<typeof ExamAssigningSchema>;

interface ExamAssigningFormModalProps {
  open: boolean;
  onClose: () => void;
  onAssignExams: (examCourseCodeIds: string[]) => Promise<void>;
}

export default function ExamAssigningFormModal(props: Readonly<ExamAssigningFormModalProps>) {
  const { open: isOpen, onClose, onAssignExams } = props;

  const formContext = useForm<ExamAssigningFormInput>({
    defaultValues: {
      examCourseCodes: [],
    },
    resolver: zodResolver(ExamAssigningSchema),
  });

  const handleAssignExams = async (data: ExamAssigningFormInput) => {
    const { examCourseCodes } = data;
    await onAssignExams(examCourseCodes);
    formContext.reset();
  };

  const handleSelectAssignedExams = (selectedExamCourses: string[]) => {
    formContext.setValue('examCourseCodes', selectedExamCourses, { shouldDirty: true });
  };

  const handleClose = () => {
    onClose();
    formContext.reset();
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='lg'
        slotProps={{
          paper: {
            component: Form,
            onSubmit: formContext.handleSubmit(handleAssignExams),
          },
        }}
      >
        <DialogTitle onClose={handleClose}>Assign Exam</DialogTitle>
        <Divider />
        <DialogContent>
          <ExamAssigningList onSelectRow={handleSelectAssignedExams} />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button size='large' variant='outlined' color='secondary' onClick={handleClose}>
            Cancel
          </Button>
          <FormButton label='Assign Exam' size='large' />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
